// The always-visible "Report a problem" button. Shows a count of failures
// (errors, failed requests) captured on this page so far.

import { isFailure } from '../shared/timeline.js';

const CSS = `
.ab-launch {
  position: fixed;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 10px 14px;
  border: 0;
  border-radius: 999px;
  background: var(--ab-accent);
  color: var(--ab-accent-text);
  font-weight: 600;
  font-size: 14px;
  box-shadow: var(--ab-shadow);
  transition: transform 120ms ease, opacity 120ms ease;
}
.ab-launch:hover { transform: translateY(-1px); }
.ab-launch.ab-right { right: 20px; }
.ab-launch.ab-left { left: 20px; }
.ab-launch svg { width: 18px; height: 18px; flex: none; }
.ab-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ffffff;
  color: var(--ab-danger);
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  font-weight: 700;
}
`;

const ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2l1.9 1.9"/><path d="M14.1 3.9L16 2"/><path d="M9 7.1V6a3 3 0 1 1 6 0v1.1"/><path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z"/><path d="M12 20v-9"/><path d="M6.5 13H3"/><path d="M21 13h-3.5"/><path d="M6 17l-3 2"/><path d="M18 17l3 2"/><path d="M6 9L3 7"/><path d="M18 9l3-2"/></svg>`;

export function mountButton(ctx) {
  const { container, addStyles } = ctx.host;
  addStyles(CSS);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = `ab-launch ${ctx.config.position === 'bottom-left' ? 'ab-left' : 'ab-right'}`;
  button.setAttribute('part', 'launcher');
  button.innerHTML = `${ICON}<span>Report a problem</span><span class="ab-badge" hidden></span>`;
  const badge = /** @type {HTMLElement} */ (button.querySelector('.ab-badge'));
  container.appendChild(button);

  let failures = ctx.entries().filter((e) => e.page === ctx.pageId && isFailure(e)).length;
  const render = () => {
    badge.hidden = failures === 0;
    badge.textContent = failures > 99 ? '99+' : String(failures);
    button.setAttribute(
      'aria-label',
      failures ? `Report a problem (${failures} issues captured on this page)` : 'Report a problem',
    );
  };
  render();
  ctx.subscribe((entry) => {
    if (isFailure(entry)) {
      failures++;
      render();
    }
  });

  button.addEventListener('click', () => {
    button.disabled = true;
    ctx
      .open()
      .catch((error) => {
        // The panel couldn't load (asset host down, CSP). Tell the user rather than fail silently.
        button.querySelector('span').textContent = 'Report tool unavailable';
        button.title = String(error && error.message);
      })
      .finally(() => {
        button.disabled = false;
      });
  });

  ctx.launcher = {
    element: button,
    show() {
      button.hidden = false;
    },
    hide() {
      button.hidden = true;
    },
  };
  return ctx.launcher;
}
